import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Reemplazo de `useMantineColorScheme`/`useComputedColorScheme` · misma UX de
 * 3 modos que ya tenía `ThemeToggle.tsx` (Claro / Sistema / Oscuro).
 *
 * Mantine hacía esto internamente (persistencia + `data-mantine-color-scheme`
 * en `<html>` + `ColorSchemeScript` anti-flash). Acá el equivalente es:
 *   - Este store (Zustand + persist, mismo patrón que `auth-store.ts`) guarda
 *     la PREFERENCIA del usuario (`mode`).
 *   - `resolvedTheme` = la preferencia ya resuelta contra
 *     `matchMedia("(prefers-color-scheme: dark)")` cuando `mode === "system"`.
 *   - `applyResolvedTheme` pone/quita la clase `dark` en `<html>` — de ahí
 *     cuelga el variant `dark:` de Tailwind (`@custom-variant dark` en
 *     `globals.css`).
 *   - El script inline en `index.html` hace el mismo cálculo ANTES de que
 *     React monte, para no pintar el tema equivocado un frame (igual que
 *     `ColorSchemeScript`).
 */
export type ThemeMode = "light" | "dark" | "system";

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

/** Resuelve el modo guardado contra el sistema cuando aplica. */
export function resolveTheme(mode: ThemeMode): "light" | "dark" {
  if (mode === "system") return systemPrefersDark() ? "dark" : "light";
  return mode;
}

/** Aplica la clase `dark` en `<html>` — único punto de verdad del DOM. */
export function applyResolvedTheme(resolved: "light" | "dark"): void {
  document.documentElement.classList.toggle("dark", resolved === "dark");
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: "system",
      setMode: (mode) => {
        set({ mode });
        applyResolvedTheme(resolveTheme(mode));
      },
    }),
    {
      name: "multiva-theme",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

/**
 * Hook de conveniencia: modo guardado + el resuelto (para pintar el ícono
 * correcto en el toggle cuando `mode === "system"`, igual que
 * `useComputedColorScheme` hacía con Mantine).
 */
export function useResolvedTheme(): "light" | "dark" {
  const mode = useThemeStore((s) => s.mode);
  return resolveTheme(mode);
}

/**
 * Suscribe el listener de `matchMedia` para que, con `mode === "system"`, un
 * cambio de tema del SO en caliente actualice la clase `dark` sin recargar.
 * Se llama una sola vez desde `main.tsx`. Devuelve el cleanup.
 */
export function watchSystemTheme(): () => void {
  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (useThemeStore.getState().mode === "system") {
      applyResolvedTheme(resolveTheme("system"));
    }
  };
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
